import { Box, Button, Dialog, DialogActions, Grid, IconButton, Link } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { styled } from "styled-components";
import { brandRed, validationGreen, validationRed } from "theme/colors";
import { useCallback, useState } from "react";
import { Aligned } from "components/Layout";
import { megabytesToBytes } from "lib/filesize";
import { isEmpty, uniq } from "lodash";
import { useDispatch } from "react-redux";
import { useDropzone } from "react-dropzone";
import { grey2 } from "theme/colors";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { addRule } from "actions/rules";
import { IStructuredError, IUploadRuleData } from "store/types";
import { getUser } from "lib/Cookies";
import { get } from "lib/fetch";
import { getConfig } from "actions/config";

interface IProps {
  open: boolean;
  onConfirm: () => any;
  onClose: () => any;
  onError: (errors: IStructuredError[]) => any;
  lastVersion: number;
}

// Based on https://react-dropzone.js.org/#section-styling-dropzone
const getBorder = (props: any) => {
  if (props.isDragAccept) {
    return `2px dashed ${validationGreen.toString()}`;
  }
  if (props.isDragReject) {
    return `2px dashed ${validationRed.toString()}`;
  }
  return "1px dashed rgba(0, 0, 0, 0.25)";
};
const getBackground = (props: any) => {
  if (props.isDragAccept) {
    return validationGreen.lighten(0.55).toString();
  }
  if (props.isDragReject) {
    return validationRed.lighten(0.4).toString();
  }
  if (props.isFocused) {
    return grey2.toString();
  }
  return "none";
};

const style = {
  height: "330px",
  width: "530px",
  bgcolor: "#FFFFFF",
  boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.16)",
  borderRadius: "8px",
  p: 4,
};

const SelectedFileContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 24px 16px 32px;
  gap: 24px;
  justify-content: space-between;
  background: ${grey2.toString()};
  border-radius: 8px;
  width: 95%;
`;

const DnDContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px 16px 32px;
  gap: 24px;
  border: ${(props) => getBorder(props)};
  background: ${(props) => getBackground(props)};
  border-radius: 8px;
  cursor: pointer;
  width: 95%;
`;

const DndHeaderContainer = styled.div`
  margin-left: 20px;
`;

const DndHeaderContainerTitle = styled.p`
  font-family: "Roboto";
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  padding: 0px;
  margin: 0px;
  color: #414141;
`;

const DndHeaderContainerSubTitle = styled.p`
  font-family: "Roboto";
  font-style: normal;
  font-weight: 500;
  font-size: 13px;
  line-height: 24px;
  padding-top: 4px;
  margin: 0px;
  color: grey;
`;

const PageTitle = styled.legend`
  font-family: "Roboto", sans-serif;
  font-size: 24px;
  font-weight: 700;
  line-height: 28px;
`;

const ActionButton = styled(Button)`
  font-family: "Roboto";
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 16px;
  padding: 12px 16px;
`;

export const RulesUploadDialog = ({ open, onConfirm, onClose, onError, lastVersion }: IProps) => {
  const downloadTemplate = useCallback(async () => {
    const response = await get(`/v1/rules?format=csv`);
    const blob = new Blob([response], { type: "text/csv" });
    const anchorTag = document.createElement("a");
    anchorTag.download = "template.csv";
    anchorTag.href = URL.createObjectURL(blob);
    anchorTag.click();
  }, []);

  const [files, setFiles] = useState([] as File[]);
  const [uploading, setUploading] = useState(false);
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      setFiles(uniq([...files, ...acceptedFiles]));
    },
    [files, setFiles],
  );
  const removeFile = useCallback(
    (file: File) => {
      setFiles(files.filter((f) => f !== file));
    },
    [files, setFiles],
  );

  const dispatch = useDispatch();

  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } = useDropzone({
    onDrop,
    accept: {
      "text/csv": [".csv"],
    },
    maxSize: megabytesToBytes(10),
  });

  const uploadFiles = useCallback(async () => {
    onConfirm();
    setUploading(true);
    const payload = {
      userToken: getUser()?.email,
      format: "csv",
      version: lastVersion,
      reportOnly: false,
    } as IUploadRuleData;
    const result: any = await dispatch(addRule(files, payload));
    if (result.value.errors) {
      let errors = [] as IStructuredError[];
      // Use the default errors, but if errorsStructured is present use that instead.
      if (!isEmpty(result.value.errors) && isEmpty(result.value.errorsStructured)) {
        errors.push({ line: -1, error: result.value.errors[0], rid: 0 });
      } else if (!isEmpty(result.value.errorsStructured)) {
        errors = result.value.errorsStructured;
      } else {
        errors.push({ line: -1, error: "An unknown error occured", rid: 0 });
      }
      onError(errors);
    }
    setFiles([]);
    setUploading(false);
    dispatch(getConfig());
  }, [dispatch, files, lastVersion, onConfirm, onError]);

  return (
    <Dialog open={open} onClose={onClose} aria-labelledby="upload-dialog">
      <Box sx={style}>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={11}>
            <PageTitle>Upload CSV</PageTitle>
          </Grid>
          <Grid item xs={1} style={{ display: "flex", alignItems: "center" }}>
            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={{
                color: "#414141",
              }}
            >
              <CloseIcon />
            </IconButton>
          </Grid>
        </Grid>
        <p>Please upload a CSV containing the alignment rules.</p>
        <p>
          If you need guidance on how to structure your file, you can download the{" "}
          <Link style={{ cursor: "pointer", color: brandRed.toString() }} onClick={downloadTemplate}>
            CSV template
          </Link>
          .
        </p>
        <Grid container spacing={3} style={{ width: "100%", padding: "10px" }}>
          {files.length < 1 && (
            <Grid item xs={12}>
              <DnDContainer {...getRootProps({ isFocused, isDragAccept, isDragReject })}>
                {/* <pre>{JSON.stringify(getInputProps(), null, 2)}</pre> */}
                <input {...getInputProps()} />
                <Aligned>
                  <CloudUploadIcon sx={{ fontSize: "48px", fill: grey2.toString() }} />
                  <DndHeaderContainer>
                    <DndHeaderContainerTitle>Select a file or drag and drop here.</DndHeaderContainerTitle>
                    <DndHeaderContainerSubTitle>CSV, file size no more than 10MB.</DndHeaderContainerSubTitle>
                  </DndHeaderContainer>
                </Aligned>
                <Button variant="outlined">Select File</Button>
              </DnDContainer>
            </Grid>
          )}
          {files.map((f, i) => (
            <Grid key={i} item xs={12}>
              <SelectedFileContainer>
                <Aligned style={{ gap: "24px" }}>
                  <InsertDriveFileIcon />
                  {f.name}
                </Aligned>
                <Aligned style={{ gap: "24px" }}>
                  <Button onClick={() => removeFile(f)}>Remove</Button>
                </Aligned>
              </SelectedFileContainer>
            </Grid>
          ))}
        </Grid>
        <DialogActions sx={{ paddingRight: "0px", paddingTop: "24px", paddingBottom: "16px" }}>
          <ActionButton variant="outlined" onClick={onClose}>
            Cancel
          </ActionButton>
          <ActionButton variant="contained" onClick={uploadFiles} disabled={files.length !== 1 || uploading}>
            Save
          </ActionButton>
        </DialogActions>
      </Box>
    </Dialog>
  );
};
